import React from "react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import { COLORS } from "../../constants";
import type { UndMedidaOption } from "../../utils/controleProducaoQuantidade";

export type UndMedidaSelectProps = {
  unds: UndMedidaOption[];
  value: number | "";
  onChange: (id: number | "") => void;
  disabled?: boolean;
  className?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
  compact?: boolean;
};

type UndOpt = { value: number; label: string };

function selectStyles(compact: boolean): StylesConfig<UndOpt, false> {
  const altura = compact ? 28 : 32;
  return {
    control: (base, state) => ({
      ...base,
      minHeight: altura,
      height: altura,
      fontSize: compact ? "0.6875rem" : "0.75rem",
      borderColor: COLORS.NEUTRAL_300,
      backgroundColor: state.isDisabled ? COLORS.NEUTRAL_100 : COLORS.WHITE,
      boxShadow: "none",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      "&:hover": {
        borderColor: state.isDisabled ? COLORS.NEUTRAL_300 : COLORS.PRIMARY,
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: compact ? "0 4px" : "0 6px",
      justifyContent: compact ? "center" : "flex-start",
    }),
    singleValue: (base) => ({
      ...base,
      margin: 0,
      maxWidth: "100%",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      paddingRight: compact ? 0 : 2,
    }),
    dropdownIndicator: (base) =>
      compact
        ? { ...base, display: "none" }
        : { ...base, padding: 2 },
    indicatorSeparator: () => ({
      display: "none",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 10050,
    }),
    option: (base, state) => ({
      ...base,
      fontSize: "0.8125rem",
      backgroundColor: state.isSelected
        ? COLORS.PRIMARY
        : state.isFocused
          ? COLORS.NEUTRAL_100
          : COLORS.WHITE,
      color: state.isSelected ? COLORS.WHITE : COLORS.NEUTRAL_900,
    }),
  };
}

const SELECT_STYLES = selectStyles(false);

const UndMedidaSelect: React.FC<UndMedidaSelectProps> = ({
  unds,
  value,
  onChange,
  disabled = false,
  className = "",
  allowEmpty = false,
  emptyLabel = "UND",
  compact = false,
}) => {
  const options = unds.map((und) => ({ value: und.id, label: und.nome }));
  const selected =
    value === "" ? null : options.find((opt) => opt.value === value) ?? null;

  return (
    <div className={className}>
      <ReactSelect<UndOpt, false>
        options={options}
        value={selected}
        isDisabled={disabled}
        isClearable={allowEmpty}
        isSearchable={options.length > 6}
        placeholder={emptyLabel}
        onChange={(opt: SingleValue<UndOpt>) =>
          onChange(opt?.value ?? "")
        }
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        menuPosition="fixed"
        styles={compact ? selectStyles(true) : SELECT_STYLES}
        classNamePrefix="und-select"
        aria-label="Unidade de medida"
      />
    </div>
  );
};

export default UndMedidaSelect;
